import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

interface ThemeConfig {
  // Primary colors
  primary?: string;
  primaryForeground?: string;
  // Secondary colors
  secondary?: string;
  secondaryForeground?: string;
  // Accent colors
  accent?: string;
  accentForeground?: string;
  // Gold/brand colors
  gold?: string;
  goldLight?: string;
  goldDark?: string;
  // Background variants
  background?: string;
  foreground?: string;
  // Card colors
  card?: string;
  cardForeground?: string;
  // Muted colors
  muted?: string;
  mutedForeground?: string;
  // Border and ring
  border?: string;
  ring?: string;
  // Custom CSS for special effects
  customCss?: string;
  // Hero banner overlay
  heroOverlay?: string;
  // Button variants
  buttonPrimary?: string;
  buttonPrimaryHover?: string;
}

interface OccasionTheme {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  theme_config: ThemeConfig;
}

interface OccasionThemeContextType {
  activeTheme: OccasionTheme | null;
  isLoading: boolean;
  themeName: string | null;
}

const OccasionThemeContext = createContext<OccasionThemeContextType>({
  activeTheme: null,
  isLoading: true,
  themeName: null,
});

export const useOccasionTheme = () => useContext(OccasionThemeContext);

interface OccasionThemeProviderProps {
  children: ReactNode;
}

export const OccasionThemeProvider: React.FC<OccasionThemeProviderProps> = ({ children }) => {
  const [activeTheme, setActiveTheme] = useState<OccasionTheme | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchActiveTheme = async () => {
      try {
        console.log("[OccasionTheme] Fetching active theme...");
        
        // First check for manually activated themes
        const { data: manualTheme, error: manualError } = await supabase
          .from("occasion_themes")
          .select("*")
          .eq("is_active", true)
          .maybeSingle();

        console.log("[OccasionTheme] Query result:", { manualTheme, manualError });

        if (manualError) {
          console.error("[OccasionTheme] Error fetching active theme:", manualError);
          setIsLoading(false);
          return;
        }

        if (manualTheme) {
          // If is_active is true, apply the theme immediately (manual control)
          // Date validation is optional - only check if dates are set and you want date-based auto-activation
          console.log("[OccasionTheme] Applying theme:", manualTheme.slug);
          setActiveTheme(manualTheme as OccasionTheme);
          applyTheme(manualTheme.theme_config as ThemeConfig);
        } else {
          console.log("[OccasionTheme] No active theme found");
        }
      } catch (error) {
        console.error("[OccasionTheme] Error fetching theme:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchActiveTheme();
  }, []);

  const applyTheme = (config: ThemeConfig) => {
    const root = document.documentElement;
    
    // Apply CSS variable overrides
    if (config.primary) root.style.setProperty("--primary", config.primary);
    if (config.primaryForeground) root.style.setProperty("--primary-foreground", config.primaryForeground);
    if (config.secondary) root.style.setProperty("--secondary", config.secondary);
    if (config.secondaryForeground) root.style.setProperty("--secondary-foreground", config.secondaryForeground);
    if (config.accent) root.style.setProperty("--accent", config.accent);
    if (config.accentForeground) root.style.setProperty("--accent-foreground", config.accentForeground);
    if (config.gold) root.style.setProperty("--gold", config.gold);
    if (config.goldLight) root.style.setProperty("--gold-light", config.goldLight);
    if (config.goldDark) root.style.setProperty("--gold-dark", config.goldDark);
    if (config.background) root.style.setProperty("--background", config.background);
    if (config.foreground) root.style.setProperty("--foreground", config.foreground);
    if (config.card) root.style.setProperty("--card", config.card);
    if (config.cardForeground) root.style.setProperty("--card-foreground", config.cardForeground);
    if (config.muted) root.style.setProperty("--muted", config.muted);
    if (config.mutedForeground) root.style.setProperty("--muted-foreground", config.mutedForeground);
    if (config.border) root.style.setProperty("--border", config.border);
    if (config.ring) root.style.setProperty("--ring", config.ring);
    if (config.heroOverlay) root.style.setProperty("--hero-overlay", config.heroOverlay);
    if (config.buttonPrimary) root.style.setProperty("--button-primary", config.buttonPrimary);
    if (config.buttonPrimaryHover) root.style.setProperty("--button-primary-hover", config.buttonPrimaryHover);

    // Apply custom CSS if provided
    if (config.customCss) {
      let styleEl = document.getElementById("occasion-theme-styles");
      if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.id = "occasion-theme-styles";
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = config.customCss;
    }

    // Add theme class to body
    document.body.setAttribute("data-occasion-theme", "active");
  };

  return (
    <OccasionThemeContext.Provider
      value={{
        activeTheme,
        isLoading,
        themeName: activeTheme?.name || null,
      }}
    >
      {children}
    </OccasionThemeContext.Provider>
  );
};
